// Super-admin JIRA integration settings.
//
// /super/integrations/jira
//
// CRUD over the single-row jira_integration_config table.  The API
// token is write-only — the GET response carries `api_token_set:true`
// when ciphertext is present, never the plaintext.  Leave the token
// field blank to keep the current value; enter a new value to rotate.

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, TextField, Switch, Button,
  CircularProgress, Alert as MuiAlert, FormControlLabel, Snackbar,
} from "@mui/material";

import bugReportsAdmin from "../../../../utils/axios/v1/bugReportsAdminAxios";
import PageHeader from "../../../../components/pages/_new/super/common/PageHeader";
import colors from "../../../../utils/colors";

export default function T_JiraSettings() {
  const [loaded, setLoaded] = useState(false);
  const [cfg, setCfg] = useState({
    base_url: "", email: "", project_key: "",
    issue_type: "Bug", enabled: false,
    api_token_set: false,
  });
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);

  const refresh = async () => {
    try {
      const r = await bugReportsAdmin.getJiraConfig();
      setCfg(r.data);
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        base_url: cfg.base_url,
        email: cfg.email,
        project_key: cfg.project_key,
        issue_type: cfg.issue_type || "Bug",
        enabled: !!cfg.enabled,
      };
      if (token) body.api_token = token;
      const r = await bugReportsAdmin.updateJiraConfig(body);
      setCfg(r.data);
      setToken("");
      setSnack({ severity: "success", message: "Saved." });
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      <Helmet title="JIRA Integration — Super Admin" />
      <Box sx={{ p: 3, maxWidth: 720 }}>
        <PageHeader title="JIRA Integration" />

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          Bug reports submitted by tenants land in the super-admin
          triage page. From there a super-admin clicks "Create JIRA
          ticket" to push the report into the configured JIRA project.
          Credentials are encrypted at rest (AES-256-GCM) using
          LAYERD_CONFIG_SECRET.
        </Typography>

        <Stack spacing={2.5}>
          <TextField
            label="JIRA base URL"
            placeholder="https://yourdomain.atlassian.net"
            value={cfg.base_url}
            onChange={(e) => setCfg({ ...cfg, base_url: e.target.value })}
            fullWidth size="small"
            helperText="No trailing slash."
          />
          <TextField
            label="Account email"
            placeholder="bots@layerd.ai"
            value={cfg.email}
            onChange={(e) => setCfg({ ...cfg, email: e.target.value })}
            fullWidth size="small"
            helperText="JIRA user whose API token we'll use."
          />
          <TextField
            label="API token"
            placeholder={cfg.api_token_set ? "•••••••• (set — leave blank to keep)" : "Enter Atlassian API token"}
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            fullWidth size="small"
            helperText={cfg.api_token_set ? "Token is currently set. Type a new value to rotate, or leave blank." : "Get one from id.atlassian.com → Security → API tokens."}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Project key"
              placeholder="BUG"
              value={cfg.project_key}
              onChange={(e) => setCfg({ ...cfg, project_key: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
              helperText="Short uppercase project key from JIRA."
            />
            <TextField
              label="Issue type"
              value={cfg.issue_type || "Bug"}
              onChange={(e) => setCfg({ ...cfg, issue_type: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
              helperText="Default: Bug."
            />
          </Stack>

          <FormControlLabel
            control={<Switch
              checked={!!cfg.enabled}
              onChange={(_, v) => setCfg({ ...cfg, enabled: v })}
            />}
            label="Integration enabled"
          />

          {!cfg.enabled && (
            <MuiAlert severity="info">
              While disabled, the "Create JIRA ticket" button on the bug-reports
              page will return 400. Configure the fields above, then flip this on.
            </MuiAlert>
          )}

          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button variant="outlined" onClick={refresh} disabled={saving}>
              Reload
            </Button>
          </Stack>

          {cfg.updated_at && (
            <Typography variant="caption" color="text.secondary">
              Last updated {new Date(cfg.updated_at).toLocaleString()}{cfg.updated_by ? ` by ${cfg.updated_by}` : ""}.
            </Typography>
          )}
        </Stack>
      </Box>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        {snack && <MuiAlert severity={snack.severity}>{snack.message}</MuiAlert>}
      </Snackbar>
    </>
  );
}
